import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

export type UseSignedUrlResult = {
  url: string | null;
  hasError: boolean;
};

/**
 * Fetches a presigned read URL for a stored user upload (document_references
 * file_id / template source_file_id) and manages the request lifecycle. Returns
 * a live URL once available; best-effort — `hasError` flags a failed sign so
 * callers can fall back. No-ops until tenantId/userId/fileId are all present.
 */
export const useSignedUrl = (
  fileId: string | null | undefined,
): UseSignedUrlResult => {
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();
  const [url, setUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setUrl(null);
    setHasError(false);
    if (!tenantId || !userId || !fileId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.users.getUserFileSignedUrl({
          params: { tenantId, userId, fileId },
        });
        if (cancelled) return;
        if (res.status === 200 && res.body.presignedUrl) {
          setUrl(res.body.presignedUrl);
        } else {
          setHasError(true);
        }
      } catch {
        if (!cancelled) setHasError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // apiClient is a stable context value; intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, userId, fileId]);

  return { url, hasError };
};
